"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dismissAlertRequestSchema = exports.alertFindingRowSchema = exports.ALERT_SUBJECT_TYPES = exports.alertExceptionSchema = exports.ALERT_TOPICS = exports.RISK_BANDS = exports.alertKindSchema = exports.RETIRED_ALERT_KINDS = exports.ALERT_KIND_LIST = exports.ALERT_KINDS = exports.ALERT_AREAS = exports.ALERT_SEVERITIES = void 0;
exports.riskScore = riskScore;
exports.riskBand = riskBand;
exports.alertIsActive = alertIsActive;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Alerts — C57, Phase 5. Twenty-six rules about the data being wrong.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EVERY RULE IS ABOUT A NUMBER ELSEWHERE BEING WRONG. THIS IS NOT A TO-DO LIST.**
 * `member_no_rate`'s impact is *"the hours go in at 0 Ft, so actual spend and margin are both
 * wrong"* — the dashboard auditing its own inputs. A rule that says *"this task has no assignee,
 * somebody should pick one"* and nothing more would belong on a board, not here.
 *
 * ⚠️⚠️ **THE PENALTY IS NOT THE SEVERITY.** `task_no_line` is high and costs 15; `ext_cost_unlinked`
 * is LOW and costs 8, more than several mid rules. Severity says how alarming one finding is;
 * the penalty says how much it undermines the FIGURES, which is what RISK measures. They were
 * chosen per rule and neither is derivable from the other.
 *
 * ⚠️ **The names, impacts and fixes are NOT here** — rule 7 puts them in the i18n catalogue under
 * `alert.<kind>.*`. This file holds what the scoring and the screens branch on.
 */
exports.ALERT_SEVERITIES = ['high', 'mid', 'low'];
/**
 * Which part of the product a rule is about — the Alertek tab filters on it.
 *
 * ⚠️ **Not the same as the resource type a finding points AT.** `alloc_no_rate` is in the
 * *Díjkártya* area and its subject is a person; the area says where somebody goes to fix it.
 */
exports.ALERT_AREAS = [
    'contract',
    'currency',
    'external_cost',
    'invoice',
    'line_item',
    'member',
    'occurrence',
    'rate_card',
    'resource',
    'schedule',
    'task',
    'worklog',
];
/**
 * ⚠️ 18 high · 16 mid · 4 low, and `alerts.test.ts` asserts that split has not drifted. The mockup's
 * 26 come first in its own order; the twelve after them are additions (rule 8) — P2-32's two,
 * ux-pass-2's `li_closed_work`, OS-1's `wlog_unidentified`, and EV-8's eight.
 */
exports.ALERT_KINDS = {
    member_no_rate: { severity: 'high', penalty: 12, area: 'rate_card', icon: 'credit-card' },
    member_no_sched: { severity: 'mid', penalty: 4, area: 'schedule', icon: 'calendar-days' },
    task_multi_line: { severity: 'high', penalty: 10, area: 'line_item', icon: 'list-checks' },
    task_no_line: { severity: 'high', penalty: 15, area: 'line_item', icon: 'list-checks' },
    li_no_budget: { severity: 'mid', penalty: 4, area: 'line_item', icon: 'file-text' },
    contract_expired_log: { severity: 'mid', penalty: 8, area: 'contract', icon: 'file-text' },
    wlog_duplicate: { severity: 'mid', penalty: 6, area: 'worklog', icon: 'clock' },
    inactive_member_log: { severity: 'mid', penalty: 5, area: 'member', icon: 'user-round' },
    ext_cost_unlinked: { severity: 'low', penalty: 8, area: 'external_cost', icon: 'credit-card' },
    license_not_invoiced: { severity: 'mid', penalty: 6, area: 'line_item', icon: 'credit-card' },
    task_no_assignee: { severity: 'low', penalty: 3, area: 'task', icon: 'list-checks' },
    over_invoiced: { severity: 'high', penalty: 8, area: 'invoice', icon: 'file-text' },
    task_no_estimate: { severity: 'high', penalty: 10, area: 'task', icon: 'list-checks' },
    task_no_dates: { severity: 'mid', penalty: 6, area: 'task', icon: 'calendar-days' },
    li_no_window: { severity: 'high', penalty: 8, area: 'line_item', icon: 'file-text' },
    li_no_rate_tm: { severity: 'high', penalty: 8, area: 'line_item', icon: 'file-text' },
    fx_missing: { severity: 'high', penalty: 10, area: 'currency', icon: 'credit-card' },
    alloc_no_rate: { severity: 'high', penalty: 8, area: 'rate_card', icon: 'credit-card' },
    wlog_outside_li: { severity: 'mid', penalty: 6, area: 'worklog', icon: 'clock' },
    wlog_no_occ: { severity: 'high', penalty: 10, area: 'occurrence', icon: 'calendar-days' },
    wlog_multi_occ: { severity: 'high', penalty: 10, area: 'occurrence', icon: 'calendar-days' },
    dep_date_violation: { severity: 'mid', penalty: 6, area: 'task', icon: 'list-checks' },
    stale_progress: { severity: 'mid', penalty: 6, area: 'task', icon: 'list-checks' },
    log_on_todo: { severity: 'low', penalty: 3, area: 'task', icon: 'clock' },
    no_recent_logs: { severity: 'mid', penalty: 6, area: 'worklog', icon: 'clock' },
    plan_thin: { severity: 'low', penalty: 3, area: 'resource', icon: 'layout-grid' },
    /**
     * P2-32.5 · an issue from a remote project this project syncs with, that no line item claims. It
     * has no project of its own, so it is raised on EVERY project with a line item targeting that
     * remote project — whichever of them should have it, all of them are the place to notice.
     */
    task_unrouted: { severity: 'high', penalty: 10, area: 'line_item', icon: 'list-checks' },
    /** P2-32.6 · a synced task whose rule stopped claiming it. It keeps its line item and hours. */
    task_orphaned: { severity: 'high', penalty: 8, area: 'line_item', icon: 'list-checks' },
    /**
     * ⚠️⚠️ **ADDED UNDER RULE 8, 14 OCT 2026 (ux-pass-2).** Work reached a line item after it was
     * closed: a task created on it or moved onto it, or an hour dated after the close or entered after
     * it. Closing blocks nothing on the server — the product owner asked for an alert instead, and the
     * invoice dialog's *Lezárjuk a sortételt?* hint promises one. Graded beside `wlog_outside_li`, its
     * nearest sibling (an hour the line item's frame does not cover): mid, 6.
     */
    li_closed_work: { severity: 'mid', penalty: 6, area: 'line_item', icon: 'clock' },
    /**
     * ⚠️⚠️ **ADDED UNDER RULE 8, 20 OCT 2026 (OS-1).** Somebody's hours on this project bill to no line
     * item: every line item that could take them names other people, or names them outside its
     * window. One finding per PERSON, not per hour — the fix is one (name them on a line item, or
     * widen its window), and a finding a day would cap the RISK score for one person's month. Graded
     * beside `wlog_outside_li`, an hour its frame does not cover: mid, 6.
     */
    wlog_unidentified: { severity: 'mid', penalty: 6, area: 'worklog', icon: 'user-round' },
    /**
     * ⚠️⚠️ **ADDED UNDER RULE 8, 30 OCT 2026 (EV-8).** The five EACs' data gaps, which first drew as
     * a banner above both dashboard tabs; the product owner moved them here — *"this should be with
     * the alerts"*. One finding per PROJECT, read off the whole project's evaluation (`eac.ts`), so
     * each carries a share or a count rather than a subject of its own. The five reds make CPI and
     * the EAC unreadable and are graded beside `li_no_window` (high, 8); the three yellows disable
     * nothing and sit beside `member_no_sched` (mid, 4). The spec's `fxMissing` is not here —
     * `fx_missing` already says it.
     */
    wlog_unestimated_share: { severity: 'high', penalty: 8, area: 'worklog', icon: 'clock' },
    wlog_unbound_share: { severity: 'high', penalty: 8, area: 'worklog', icon: 'clock' },
    li_no_tasks: { severity: 'high', penalty: 8, area: 'line_item', icon: 'file-text' },
    task_reestimated_closed: { severity: 'high', penalty: 8, area: 'task', icon: 'list-checks' },
    li_rule_on_status: { severity: 'high', penalty: 8, area: 'line_item', icon: 'file-text' },
    alloc_stale: { severity: 'mid', penalty: 4, area: 'resource', icon: 'layout-grid' },
    li_over_plan: { severity: 'mid', penalty: 4, area: 'resource', icon: 'layout-grid' },
    member_silent: { severity: 'mid', penalty: 4, area: 'resource', icon: 'user-round' },
};
/** ⚠️ The order the catalogue is written in, which is the mockup's own. */
exports.ALERT_KIND_LIST = Object.keys(exports.ALERT_KINDS);
/**
 * ⚠️⚠️ 5 Nov 2026, the product owner · **KINDS NO SCAN RAISES ANY MORE, KEPT IN THE CATALOGUE.**
 * `ext_cost_unlinked` described a cost on the project and nothing finer, which `0257` abolished.
 * It stays a kind because findings already stored carry it and must still parse (and read in the
 * history); the next scan resolves any still open, since nothing raises it again. The roll-call
 * test excuses these from "every rule is reachable".
 */
exports.RETIRED_ALERT_KINDS = ['ext_cost_unlinked'];
exports.alertKindSchema = zod_1.z.enum(exports.ALERT_KIND_LIST);
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ RISK — one score per project, and the CAP is the whole design.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `score = 100 − Σ min(count × penalty, 3 × penalty)`
 *
 * The per-kind cap is what stops one bulk mistake flattening the scale on its own: a project
 * where forty tasks have no estimate is in trouble, but it is not forty times the trouble of a
 * project where four do, and without the cap every such project scores zero and the number stops
 * distinguishing anything.
 *
 * ⚠️ **Only OPEN findings count.** A resolved one is a statement about the past, and a dismissed
 * one is suppressed by a standing exception (P5-5) — neither is a reason to distrust a figure.
 */
function riskScore(findings) {
    const counted = new Map();
    for (const finding of findings) {
        counted.set(finding.kind, (counted.get(finding.kind) ?? 0) + 1);
    }
    const perKind = [...counted.entries()].map(([kind, count]) => {
        const penalty = exports.ALERT_KINDS[kind].penalty;
        return { kind, count, deduction: Math.min(count * penalty, 3 * penalty) };
    });
    const deduction = perKind.reduce((total, row) => total + row.deduction, 0);
    /* ⚠️ Floored at zero: a score below it would be a number nobody can read against the bands. */
    return { score: Math.max(0, 100 - deduction), deduction, perKind };
}
/**
 * ⚠️ **≥ 85 rendben · ≥ 60 figyelendő · < 60 kockázatos**, and the boundaries are the mockup's.
 * Read top-down, so the first match wins.
 */
exports.RISK_BANDS = [
    { min: 85, band: 'ok' },
    { min: 60, band: 'watch' },
    { min: 0, band: 'risky' },
];
function riskBand(score) {
    return exports.RISK_BANDS.find((row) => score >= row.min)?.band ?? 'risky';
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ P5-4 · WHAT THE SCAN DEPENDS ON. The rescan rides R-19's topics.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The ruling was *"anything that would affect any single number that alerts depend on"*, and this
 * is that list — arrived at from the predicates rather than from the sentence: every one of the
 * fourteen feeds at least one of the twenty-six rules.
 *
 * ⚠️⚠️ **DECLARED HERE, BESIDE THE RULES, AND NOT IN THE SERVICE.** A rule added in
 * `scan.ts` that reads something new needs its topic in this list, and the two sitting in one
 * package is what makes that a thing a reader notices. A dependency list in the API would drift
 * the first time somebody added a predicate.
 *
 * ⚠️ **`holiday` and `leave` are here because of P5-3**, which is easy to miss: the thresholds
 * count WORKING days, so a public holiday being added or somebody's leave being approved changes
 * how old a task is without touching the task.
 *
 * ⚠️ **Three rules age on their own and no topic can announce that** — `stale_progress`,
 * `no_recent_logs` and `contract_expired_log` become true because time passed. P5-4 says a
 * periodic pass exists for those three or not at all.
 */
exports.ALERT_TOPICS = [
    'task',
    'worklog',
    'lineitem',
    'contract',
    'invoice',
    'extcost',
    'allocation',
    'member',
    'ratecard',
    'schedule',
    'currency',
    'project',
    /* P5-3 · both of these change what a working day is. */
    'holiday',
    'leave',
];
/* ── the wire, C59 ────────────────────────────────────────────────────────────────────────── */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE SCHEMA FOR A STORED FINDING, AND THE SCREEN INFERS FROM IT.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * CLAUDE.md §3: *"Every request and response shape is one schema, defined once; the server
 * validates with it and the client infers from it."* `AlertService` declared its own
 * `StoredFinding` interface while C58 had no screen, which was fine for exactly as long as nothing
 * read it over HTTP.
 *
 * ⚠️ **`values` is `Record<string, string>` and the strings are ALREADY FORMATTED.** `8:00`, not
 * `8`; `120000.00`, not a number. The scan formats because it holds the helpers; the screen
 * interpolates into `alert.<kind>.body` and must never have to decide what `6` meant.
 */
exports.alertExceptionSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `alert_exception_2e3f4a5b` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('alertException'),
    reason: zod_1.z.string(),
    /** The member who wrote it, resolved to a display name by the server. */
    byName: zod_1.z.string(),
    at: zod_1.z.string(),
});
exports.ALERT_SUBJECT_TYPES = [
    'member',
    'task',
    'line_item',
    'contract',
    'worklog',
    'external_cost',
    'currency',
    'project',
];
exports.alertFindingRowSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `alert_6c7d8e9f` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('alert'),
    kind: exports.alertKindSchema,
    key: zod_1.z.string(),
    /** ⚠️ Two states, never three. A dismissal is the `exception`, not a status — P5-5. */
    status: zod_1.z.enum(['open', 'resolved']),
    firstDetectedAt: zod_1.z.string(),
    /** ⚠️ Set exactly when `status` is `resolved`; `0154` has a CHECK saying so. */
    resolvedAt: zod_1.z.string().nullable(),
    subjectType: zod_1.z.enum(exports.ALERT_SUBJECT_TYPES),
    subjectId: zod_1.z.string(),
    subjectLabel: zod_1.z.string(),
    /**
     * ┌──────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ WHERE TO OPEN THE OFFENDING RECORD. Built by the SERVER, which knows.      │
     * └──────────────────────────────────────────────────────────────────┘
     *
     * C59 sent the kebab to a project TAB for line items, contracts and costs, on the reading that
     * those four had no address of their own. Three of them do — `/contracts/:code`,
     * `/contracts/:code/:number` and `/costs/:number` — and what they actually need is a code and a
     * number the FINDING does not carry but the database does. So the server resolves it in the same
     * query that lists them.
     *
     * ⚠️ **`null` where there genuinely is no screen.** A worklog has no detail route, and a
     * contract that C56 left without a code cannot be addressed by one; the client falls back to the
     * project tab that holds it, which is the honest answer rather than a dead link.
     *
     * ⚠️ **Workspace-relative**, without the workspace prefix — every other address the client
     * navigates to is written that way and `useWorkspaceNavigate` adds the rest.
     */
    subjectPath: zod_1.z.string().nullable(),
    values: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
    exception: exports.alertExceptionSchema.nullable(),
});
/**
 * ⚠️ **A reason is required, and that was P5-5's whole trade.** An exception is a standing
 * statement that a rule does not apply here; an unexplained one is a silence with a name on it,
 * and the next person to read the list cannot tell it from a mistake.
 */
exports.dismissAlertRequestSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1).max(500),
});
/**
 * ⚠️⚠️ **WHAT THE ALERTEK TAB'S THREE VIEWS MEAN, and it is not the mockup's reading.** The
 * prototype had one flag — `resolved` — so *Aktív* and *Archivált* split on it. P5-5 separated the
 * dismissal from the finding, which leaves a finding that is open AND suppressed: still true, but
 * somebody has said it is fine here.
 *
 * So the split is **what is counting against RISK right now**:
 *
 * - `active`   — open, no exception. These are the ones the score is made of.
 * - `archived` — resolved (the data was fixed) **or** suppressed by an exception. Both are out of
 *   the score, and putting them together is what makes the exceptions reviewable without a
 *   settings screen nobody opens.
 * - `all`      — everything.
 *
 * ⚠️ The ÁLLAPOT column still tells the two apart, because *fixed* and *waived* are different
 * things and a reader who cannot distinguish them cannot audit either.
 */
function alertIsActive(finding) {
    return finding.status === 'open' && finding.exception === null;
}
//# sourceMappingURL=contract.js.map